import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Copy, 
  Check
} from 'lucide-react';
import ReferralAPI from '../services/referralApi';
import { useAuth } from '../context/AuthContext';

const CoopPromotionPage = () => {
  const { user } = useAuth();
  const [invitationLink, setInvitationLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({
    successfulInvites: 0,
    totalPoints: 0,
    invitedUsers: []
  });

  // 生成邀请链接
  useEffect(() => {
    const load = async () => {
      try {
        const inviterId = (user && (user.id?.toString() || user.phone)) || 'guest';
        const myCodeResp = await ReferralAPI.getOrCreateMyInviteCode(inviterId);
        const code = myCodeResp?.invite_code;
        const origin = window.location.origin;
        setInvitationLink(`${origin}/invite?inviteCode=${code}`);

        const s = await ReferralAPI.getStats(inviterId);
        setStats({
          successfulInvites: s.total_success || 0,
          totalPoints: s.total_points || 0,
          invitedUsers: s.invited_users || []
        });
      } catch (e) {
        console.warn('加载邀请信息失败', e);
      }
    };
    load();
  }, [user]);


    // 话术数组
  const promotionTexts = [
    '正在找工作 / 准备跳槽？别让简历石沉大海、面试慌到卡壳！职唛 AI 专门做面试辅导，从简历优化到模拟面试、面试押题再到面试辅助全搞定，AI 智能分析简历问题，比人工高效多了～ 通过我链接注册，面试拿高薪Offer更轻松，👉 [邀请链接]（电脑端打开体验更佳）'
  ];


// 复制邀请链接
  const handleCopyLink = async () => {
    try {
      // 随机选择一个话术
      const randomIndex = Math.floor(Math.random() * promotionTexts.length);
      const selectedText = promotionTexts[randomIndex];
      // 将[邀请链接]替换为实际的邀请链接
      const textToCopy = selectedText.replace('[邀请链接]', invitationLink);
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      if (typeof window !== 'undefined') {
        window._hmt = window._hmt || [];
        window._hmt.push(['_trackEvent', 'coop-promotion', 'copy-invite-text']);
      }
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // 降级方案
      const randomIndex = Math.floor(Math.random() * promotionTexts.length);
      const selectedText = promotionTexts[randomIndex];
      const textToCopy = selectedText.replace('[邀请链接]', invitationLink);
      
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      if (typeof window !== 'undefined') {
        window._hmt = window._hmt || [];
        window._hmt.push(['_trackEvent', 'coop-promotion', 'copy-invite-text']);
      }
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const invitedCount = stats.successfulInvites;

  return (
    <PageContainer>
      {/* 页面标题 */}
      <PageHeader>
        <PageTitle>邀请好友，赚积分</PageTitle>
      </PageHeader>

      {/* 邀请链接区域 */}
      <InvitationSection>
        <SectionTitle>您的邀请链接</SectionTitle>
        <LinkContainer>
          <LinkInput 
            value={invitationLink} 
            readOnly 
            placeholder="正在生成邀请链接..."
          />
          <CopyButton onClick={handleCopyLink} copied={copied}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? '已复制' : '复制'}
          </CopyButton>
        </LinkContainer>
      </InvitationSection>

      {/* 统计卡片 */}
      <StatsSection>
        <StatsGrid>
          <StatCard>
            <StatContent>
              <StatLabel>累计积分</StatLabel>
              <StatValue>{stats.totalPoints}</StatValue>
            </StatContent>
          </StatCard>
          <StatCard>
            <StatContent>
              <StatLabel>首付费成功人数</StatLabel>
              <StatValue>{invitedCount}</StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>
      </StatsSection>

      {/* 邀请明细 */}
      <OrdersSection>
        <SectionTitle>邀请明细（首付费成功）</SectionTitle>
        <OrdersTable>
          <TableHeader>
            <TableHeaderCell>好友</TableHeaderCell>
            <TableHeaderCell>邀请码</TableHeaderCell>
            <TableHeaderCell>时间</TableHeaderCell>
          </TableHeader>
          <TableBody>
            {stats.invitedUsers.length > 0 ? (
              stats.invitedUsers.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.display_label || `好友${idx + 1}`}</TableCell>
                  <TableCell>{row.invite_code}</TableCell>
                  <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))
            ) : (
              <EmptyState>
                <EmptyText>暂无首付费成功记录</EmptyText>
                <EmptySubText>快去复制链接邀请好友开通会员，赚取积分吧！</EmptySubText>
              </EmptyState>
            )}
          </TableBody>
        </OrdersTable>
      </OrdersSection>
    </PageContainer>
  );
};

// 样式组件
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 24px 40px 24px;
  
  @media (min-width: 1400px) {
    max-width: 1300px;
    padding: 16px 32px 50px 32px;
  }
  
  @media (min-width: 1600px) {
    max-width: 1400px;
    padding: 20px 48px 60px 48px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 48px;
  
  @media (min-width: 1400px) {
    margin-bottom: 40px;
  }
  
  @media (min-width: 1600px) {
    margin-bottom: 44px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  
  @media (min-width: 1400px) {
    font-size: 28px;
  }
  
  @media (min-width: 1600px) {
    font-size: 32px;
  }
`;

 

const InvitationSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  @media (min-width: 1400px) {
    padding: 36px;
    border-radius: 18px;
    margin-bottom: 28px;
  }
  
  @media (min-width: 1600px) {
    padding: 40px;
    border-radius: 20px;
    margin-bottom: 32px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  
  @media (min-width: 1400px) {
    font-size: 20px;
    margin-bottom: 20px;
  }
  
  @media (min-width: 1600px) {
    font-size: 22px;
    margin-bottom: 24px;
  }
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  
  @media (min-width: 1400px) {
    gap: 14px;
  }
  
  @media (min-width: 1600px) {
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  background: #f8f9fa;
  color: #495057;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
  }
  
  @media (min-width: 1400px) {
    padding: 14px 18px;
    border-radius: 9px;
    font-size: 15px;
  }
  
  @media (min-width: 1600px) {
    padding: 16px 20px;
    border-radius: 10px;
    font-size: 16px;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: ${props => props.copied ? '#50D17D' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.copied ? '#45c070' : 'linear-gradient(135deg, #5a67d8 0%, #6a4ba2 100%)'};
    opacity: ${props => props.copied ? 1 : 0.9};
  }
  
  @media (min-width: 1400px) {
    padding: 14px 20px;
    border-radius: 9px;
    font-size: 15px;
    gap: 8px;
  }
  
  @media (min-width: 1600px) {
    padding: 16px 24px;
    border-radius: 10px;
    font-size: 16px;
    gap: 10px;
  }
`;

const StatsSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  @media (min-width: 1400px) {
    padding: 36px;
    border-radius: 18px;
    margin-bottom: 28px;
  }
  
  @media (min-width: 1600px) {
    padding: 40px;
    border-radius: 20px;
    margin-bottom: 32px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  
  @media (min-width: 1400px) {
    gap: 24px;
  }
  
  @media (min-width: 1600px) {
    gap: 28px;
  }
`;

const StatCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (min-width: 1400px) {
    padding: 24px;
    border-radius: 14px;
    gap: 18px;
  }
  
  @media (min-width: 1600px) {
    padding: 28px;
    border-radius: 16px;
    gap: 20px;
  }
`;

 

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 4px;
  
  @media (min-width: 1400px) {
    font-size: 15px;
    margin-bottom: 6px;
  }
  
  @media (min-width: 1600px) {
    font-size: 16px;
    margin-bottom: 8px;
  }
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  
  @media (min-width: 1400px) {
    font-size: 24px;
  }
  
  @media (min-width: 1600px) {
    font-size: 28px;
  }
`;

const OrdersSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  @media (min-width: 1400px) {
    padding: 36px;
    border-radius: 18px;
  }
  
  @media (min-width: 1600px) {
    padding: 40px;
    border-radius: 20px;
  }
`;

const OrdersTable = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  
  @media (min-width: 1400px) {
    border-radius: 10px;
  }
  
  @media (min-width: 1600px) {
    border-radius: 12px;
  }
  
  @media (max-width: 768px) {
    overflow-x: auto;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1.5fr;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
`;

const TableHeaderCell = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  text-align: left;
  
  @media (min-width: 1400px) {
    padding: 14px 18px;
    font-size: 15px;
  }
  
  @media (min-width: 1600px) {
    padding: 16px 20px;
    font-size: 16px;
  }
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1.5fr;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
`;

const TableCell = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: #495057;
  display: flex;
  align-items: center;
  
  @media (min-width: 1400px) {
    padding: 14px 18px;
    font-size: 15px;
  }
  
  @media (min-width: 1600px) {
    padding: 16px 20px;
    font-size: 16px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

const EmptyText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const EmptySubText = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

export default CoopPromotionPage;
